import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Image as ImageIcon, Pencil, Youtube, Video, Upload, Link, X, Loader2, AlertTriangle, CheckCircle, Cloud } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";

import { API } from "../../config/api";

// Helper to check if URL is non-persistent (local upload or preview domain)
const isNonPersistentUrl = (url) => {
  if (!url) return false;
  return url.includes('/api/uploads/') || url.includes('.preview.emergentagent.com');
};

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mediaTab, setMediaTab] = useState("upload");
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    year: new Date().getFullYear(),
    event_name: "",
    url: "",
    media_type: "image", // image, video, youtube
    youtube_id: "",
    order_index: 0
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API}/admin/gallery`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });
      if (response.ok) {
        setItems(await response.json());
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      year: new Date().getFullYear(),
      event_name: "",
      url: "",
      media_type: "image",
      youtube_id: "",
      order_index: 0
    });
    setEditingItem(null);
    setMediaTab("upload");
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || "",
        year: item.year || new Date().getFullYear(),
        event_name: item.event_name || "",
        url: item.url || "",
        media_type: item.media_type || "image",
        youtube_id: item.youtube_id || "",
        order_index: item.order_index || 0
      });
      setMediaTab("url");
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error(`File too large. Max: ${isVideo ? "50MB" : "10MB"}`);
      return;
    }

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      // Use Cloudinary upload endpoint for permanent storage
      const response = await fetch(`${API}/admin/cloudinary/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          url: data.secure_url,
          cloudinary_public_id: data.public_id,
          media_type: isVideo ? "video" : "image"
        }));
        toast.success("Uploaded to Cloudinary ✅ (Permanent storage)");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const extractYoutubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleYoutubeUrl = (url) => {
    const videoId = extractYoutubeId(url);
    if (videoId) {
      setFormData(prev => ({
        ...prev,
        url: `https://www.youtube.com/embed/${videoId}`,
        youtube_id: videoId,
        media_type: "youtube"
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.url) {
      toast.error("Please add media (upload, URL, or YouTube link)");
      return;
    }

    try {
      const endpoint = editingItem 
        ? `${API}/admin/gallery/${editingItem.id}`
        : `${API}/admin/gallery`;
      
      const response = await fetch(endpoint, {
        method: editingItem ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingItem ? "Item updated!" : "Item added!");
        setDialogOpen(false);
        resetForm();
        fetchItems();
      } else {
        toast.error("Failed to save item");
      }
    } catch (error) {
      toast.error("Error saving item");
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const response = await fetch(`${API}/admin/gallery/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        toast.success("Item deleted!");
        fetchItems();
      } else {
        toast.error("Failed to delete item");
      }
    } catch (error) {
      toast.error("Error deleting item");
    }
  };

  // Group items by year
  const itemsByYear = items.reduce((acc, item) => {
    if (!acc[item.year]) acc[item.year] = [];
    acc[item.year].push(item);
    return acc;
  }, {});

  const years = Object.keys(itemsByYear).sort((a, b) => b - a);

  const renderMediaPreview = (item) => {
    if (item.media_type === "youtube" && item.youtube_id) {
      return (
        <div className="relative w-full h-full">
          <img
            src={`https://img.youtube.com/vi/${item.youtube_id}/mqdefault.jpg`}
            alt={item.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Youtube className="w-12 h-12 text-red-600 bg-white rounded-full p-2" />
          </div>
        </div>
      );
    }
    
    if (item.media_type === "video") {
      return (
        <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
          <Video className="w-12 h-12 text-white" />
        </div>
      );
    }
    
    return (
      <img
        src={item.url}
        alt={item.title}
        className="w-full h-full object-cover"
      />
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      {/* Cloudinary Status Banner */}
      <div className="bg-green-50 border border-green-300 rounded-lg p-4 flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-green-800">Cloudinary Storage Active</p>
          <p className="text-green-700 mt-1">
            New uploads are stored permanently on Cloudinary CDN. 
            Images with <span className="inline-flex items-center gap-1 bg-amber-100 px-1 rounded text-amber-700"><AlertTriangle className="w-3 h-3" />amber badges</span> need migration.
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h1 className="font-heading text-2xl text-kerala-green">Gallery & Media</h1>
        <Button 
          className="bg-kerala-green hover:bg-kerala-green/90"
          onClick={() => openDialog()}
          data-testid="add-media-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Media
        </Button>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingItem ? "Edit Media" : "Add Gallery Media"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Media Source Tabs */}
            <div className="flex gap-2 border-b border-kerala-gold/20 pb-2">
              <button
                type="button"
                onClick={() => setMediaTab("upload")}
                className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                  mediaTab === "upload" ? "bg-kerala-green text-white" : "text-gray-500 hover:text-kerala-green"
                }`}
              >
                <Upload className="w-4 h-4 inline mr-1" /> Upload
              </button>
              <button
                type="button"
                onClick={() => setMediaTab("url")}
                className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                  mediaTab === "url" ? "bg-kerala-green text-white" : "text-gray-500 hover:text-kerala-green"
                }`}
              >
                <Link className="w-4 h-4 inline mr-1" /> URL
              </button>
              <button
                type="button"
                onClick={() => setMediaTab("youtube")}
                className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                  mediaTab === "youtube" ? "bg-kerala-green text-white" : "text-gray-500 hover:text-kerala-green"
                }`}
              >
                <Youtube className="w-4 h-4 inline mr-1" /> YouTube
              </button>
            </div>

            {/* Media Input */}
            <div className="space-y-4">
              {mediaTab === "upload" && (
                <div className="space-y-3">
                  {/* Cloudinary upload info */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                    <Cloud className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-green-700">
                      <strong>Cloudinary Storage:</strong> Files uploaded here are stored permanently on Cloudinary CDN and will never disappear.
                    </p>
                  </div>
                  <div
                    className="border-2 border-dashed border-green-400 rounded-lg p-6 text-center cursor-pointer hover:border-green-500 transition-colors bg-green-50/30"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-kerala-green animate-spin mx-auto" />
                    ) : (
                      <>
                        <Cloud className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Click to upload to Cloudinary</p>
                        <p className="text-xs text-gray-400">Images (10MB) | Videos (50MB)</p>
                        <p className="text-xs text-green-600 mt-1">✓ Permanent storage</p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              )}

              {mediaTab === "url" && (
                <div>
                  <Label>Image or Video URL</Label>
                  <Input
                    value={formData.url}
                    onChange={(e) => {
                      const url = e.target.value;
                      const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(url);
                      setFormData(prev => ({ ...prev, url, media_type: isVideo ? "video" : "image" }));
                    }}
                    placeholder="https://example.com/image.jpg"
                    className="mt-1"
                  />
                </div>
              )}

              {mediaTab === "youtube" && (
                <div>
                  <Label>YouTube URL</Label>
                  <Input
                    placeholder="https://www.youtube.com/watch?v=..."
                    onChange={(e) => handleYoutubeUrl(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}

              {/* Preview */}
              {formData.url && (
                <div className="relative border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, url: "", youtube_id: "", media_type: "image" }))}
                    className="absolute top-2 right-2 z-10 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  
                  {formData.media_type === "youtube" && formData.youtube_id ? (
                    <div className="aspect-video">
                      <iframe
                        src={formData.url}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    </div>
                  ) : formData.media_type === "video" ? (
                    <video src={formData.url} controls className="w-full h-48 object-cover" />
                  ) : (
                    <img src={formData.url} alt="Preview" className="w-full h-48 object-cover" />
                  )}
                </div>
              )}
            </div>

            {/* Other Fields */}
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  min="2000"
                  max="2030"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="event_name">Event Name</Label>
                <Input
                  id="event_name"
                  value={formData.event_name}
                  onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                  className="mt-1"
                  placeholder="e.g., Vishu 2024"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="order_index">Order (lower = first)</Label>
              <Input
                id="order_index"
                type="number"
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-kerala-green hover:bg-kerala-green/90">
                {editingItem ? "Update" : "Add"} Media
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-sm border border-kerala-gold/10 p-12 text-center">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No gallery items yet.</p>
          <p className="text-sm text-gray-400">Add images, videos, or YouTube links.</p>
        </div>
      ) : (
        years.map(year => (
          <div key={year} className="space-y-4">
            <h2 className="font-heading text-xl text-kerala-green border-b border-kerala-gold/20 pb-2">
              {year}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {itemsByYear[year].map(item => (
                <div 
                  key={item.id}
                  className={`group relative aspect-square bg-gray-100 rounded-sm overflow-hidden ${
                    isNonPersistentUrl(item.url) ? 'ring-2 ring-amber-400' : ''
                  }`}
                >
                  {renderMediaPreview(item)}
                  
                  {/* Non-persistent URL warning badge */}
                  {isNonPersistentUrl(item.url) && (
                    <div className="absolute top-1 left-1 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1 z-10">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Needs external URL</span>
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-white hover:bg-blue-600"
                        onClick={() => openDialog(item)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-white hover:bg-red-600"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-white text-xs">
                      <p className="font-medium truncate">{item.title}</p>
                      <p className="text-white/70">{item.event_name}</p>
                      {item.media_type && item.media_type !== "image" && (
                        <span className="inline-flex items-center gap-1 text-xs bg-white/20 px-1 rounded mt-1">
                          {item.media_type === "youtube" ? <Youtube className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                          {item.media_type}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
